import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api, unwrap, unwrapPage } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components, operations } from '@/api/schema';

export type ResumenItem = components['schemas']['GpcSummaryItem'];
export type Resumen = components['schemas']['GpcSummaryDetail'];
export type Bloque = components['schemas']['GpcBlock'];
export type BloqueAlta = components['schemas']['GpcBlockCreateBody'];
export type BloqueCambio = components['schemas']['GpcBlockUpdateBody'];
export type FiltrosResumenes = NonNullable<
  operations['getAdminGpcSummaries']['parameters']['query']
>;

const clave = (id: number) => ['contenido', 'resumen', id];
const ruta = (id: number) => ({ params: { path: { id: String(id) } } });

export function useResumenes(query: FiltrosResumenes) {
  return useQuery({
    queryKey: ['contenido', 'resumenes', query],
    queryFn: () =>
      unwrapPage<ResumenItem[]>(api.GET('/api/v1/admin/gpc-summaries', { params: { query } })),
    placeholderData: keepPreviousData,
  });
}

export function useResumen(id: number) {
  return useQuery({
    queryKey: clave(id),
    queryFn: () => unwrap<Resumen>(api.GET('/api/v1/admin/gpc-summaries/{id}', ruta(id))),
  });
}

export function useResumenMutaciones(id: number) {
  const qc = useQueryClient();
  const err = (e: unknown) => toast.error(mensajeDe(e));
  const ponerBloques = (blocks: Bloque[]) => {
    qc.setQueryData<Resumen>(clave(id), (r) => (r ? { ...r, blocks } : r));
  };
  const hijo = (childId: number) => ({
    params: { path: { id: String(id), childId: String(childId) } },
  });
  return {
    guardar: useMutation({
      mutationFn: (body: components['schemas']['GpcSummaryUpdateBody']) =>
        unwrap<Resumen>(api.PATCH('/api/v1/admin/gpc-summaries/{id}', { ...ruta(id), body })),
      onSuccess: (d) => {
        qc.setQueryData(clave(id), d);
        void qc.invalidateQueries({ queryKey: ['contenido', 'resumenes'] });
      },
      onError: err,
    }),
    duplicar: useMutation({
      mutationFn: () =>
        unwrap<Resumen>(api.POST('/api/v1/admin/gpc-summaries/{id}/duplicate', ruta(id))),
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['contenido'] }),
      onError: err,
    }),
    borrar: useMutation({
      mutationFn: () => unwrap(api.DELETE('/api/v1/admin/gpc-summaries/{id}', ruta(id))),
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['contenido'] }),
    }),
    agregar: useMutation({
      mutationFn: (body: BloqueAlta) =>
        unwrap<Bloque[]>(
          api.POST('/api/v1/admin/gpc-summaries/{id}/blocks', { ...ruta(id), body }),
        ),
      onSuccess: ponerBloques,
      onError: err,
    }),
    editar: useMutation({
      mutationFn: ({ childId, body }: { childId: number; body: BloqueCambio }) =>
        unwrap<Bloque[]>(
          api.PATCH('/api/v1/admin/gpc-summaries/{id}/blocks/{childId}', {
            ...hijo(childId),
            body,
          }),
        ),
      onSuccess: ponerBloques,
      onError: err,
    }),
    quitar: useMutation({
      mutationFn: (childId: number) =>
        unwrap<Bloque[]>(
          api.DELETE('/api/v1/admin/gpc-summaries/{id}/blocks/{childId}', hijo(childId)),
        ),
      onSuccess: ponerBloques,
      onError: err,
    }),
    ordenar: useMutation({
      mutationFn: (ids: number[]) =>
        unwrap<Bloque[]>(
          api.PUT('/api/v1/admin/gpc-summaries/{id}/blocks/order', { ...ruta(id), body: { ids } }),
        ),
      onSuccess: ponerBloques,
      onError: err,
    }),
  };
}
